import type { NewsLetterSubscriber, NewsLetterTemplateRecord } from '@ecowheels/types';

/**
 * Recipient picking rules for the newsletter send modal.
 *
 * Kept out of the component so the parts that decide who receives real email
 * are testable without a DOM. Getting these wrong sends mail to the wrong
 * people, which cannot be taken back.
 */

/** Combining diacritical marks, left behind by NFD once a letter is split. */
const COMBINING_MARKS = /[\u0300-\u036f]/g;

/**
 * Case- and accent-insensitive form of a value.
 *
 * Hungarian names are full of accents, and the colleague searching for
 * "Kovacs" should find "Kovács" — otherwise the search box looks broken for
 * exactly the names it exists to find. NFD splits a letter from its accent so
 * the marks can be stripped.
 */
function normalise(value: string): string {
  return value.normalize('NFD').replace(COMBINING_MARKS, '').toLowerCase().trim();
}

export function matchesSearch(subscriber: NewsLetterSubscriber, query: string): boolean {
  const needle = normalise(query);
  if (needle === '') return true;
  // Every whitespace-separated term must match somewhere, so "kovacs gmail"
  // narrows rather than widens — typing more should never return more.
  return needle
    .split(/\s+/)
    .every(
      (term) =>
        normalise(subscriber.name).includes(term) || normalise(subscriber.email).includes(term)
    );
}

export function filterSubscribers(
  subscribers: NewsLetterSubscriber[],
  query: string
): NewsLetterSubscriber[] {
  return subscribers.filter((s) => matchesSearch(s, query));
}

/** Subscribers with no review request recorded against them yet. */
export function awaitingReviewRequest(subscribers: NewsLetterSubscriber[]): NewsLetterSubscriber[] {
  return subscribers.filter((s) => !s.velemenykeroSent);
}

/** The template flagged as the review request, if one has been marked. */
export function findReviewTemplate(
  templates: NewsLetterTemplateRecord[]
): NewsLetterTemplateRecord | undefined {
  return templates.find((t) => t.isReviewRequest);
}
